// Push Notification Hook for React
// Manages browser push notification subscription and permissions

import { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export function usePushNotifications(userId) {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [subscription, setSubscription] = useState(null);

  // Check if push notifications are supported
  useEffect(() => {
    const checkSupport = () => {
      const supported = 
        'serviceWorker' in navigator && 
        'PushManager' in window &&
        'Notification' in window;
      setIsSupported(supported);
      
      if (supported) {
        setPermission(Notification.permission);
      }
    };
    
    checkSupport();
  }, []);

  // Check existing subscription
  useEffect(() => {
    const checkSubscription = async () => {
      if (!isSupported || !userId) return;

      try {
        const registration = await navigator.serviceWorker.ready;
        const sub = await registration.pushManager.getSubscription();
        
        if (sub) {
          setSubscription(sub);
          setIsSubscribed(true);
        }
      } catch (err) {
        console.error('Error checking subscription:', err);
      }
    };

    checkSubscription();
  }, [isSupported, userId]);

  // Register service worker
  const registerServiceWorker = useCallback(async () => {
    if (!('serviceWorker' in navigator)) {
      throw new Error('Service workers not supported');
    }

    try {
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/'
      });
      console.log('Service Worker registered:', registration);
      return registration;
    } catch (err) {
      console.error('Service Worker registration failed:', err);
      throw err;
    }
  }, []);

  // Get VAPID public key from server
  const getVapidPublicKey = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/push-notifications/vapid-public-key`);
      if (!response.ok) throw new Error('Failed to get VAPID key');
      const data = await response.json();
      return data.publicKey;
    } catch (err) {
      console.error('Error getting VAPID key:', err);
      throw err;
    }
  }, []);

  // Convert VAPID key to Uint8Array
  const urlBase64ToUint8Array = useCallback((base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }, []);

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!isSupported || !userId) {
      setError('Push notifications not supported or user not logged in');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      // Request permission
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);

      if (permissionResult !== 'granted') {
        throw new Error('Permission denied for notifications');
      }

      // Register service worker
      await registerServiceWorker();
      const registration = await navigator.serviceWorker.ready;

      // Get VAPID public key
      const vapidPublicKey = await getVapidPublicKey();
      const convertedKey = urlBase64ToUint8Array(vapidPublicKey);

      // Subscribe to push manager
      const sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: convertedKey
      });

      // Get browser info
      const browserInfo = navigator.userAgent.includes('Chrome') ? 'Chrome' :
                         navigator.userAgent.includes('Firefox') ? 'Firefox' :
                         navigator.userAgent.includes('Safari') ? 'Safari' :
                         navigator.userAgent.includes('Edge') ? 'Edge' : 'Unknown';

      // Send subscription to server
      const response = await fetch(`${API_URL}/api/push-notifications/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          subscription: sub.toJSON(),
          device_name: navigator.platform || 'Unknown Device',
          browser: browserInfo
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save subscription');
      }

      setSubscription(sub);
      setIsSubscribed(true);
      setLoading(false);
      return true;

    } catch (err) {
      console.error('Error subscribing:', err);
      setError(err.message);
      setLoading(false);
      return false;
    }
  }, [isSupported, userId, registerServiceWorker, getVapidPublicKey, urlBase64ToUint8Array]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    if (!subscription || !userId) return false;

    setLoading(true);
    setError(null);

    try {
      // Unsubscribe from push manager
      await subscription.unsubscribe();

      // Remove from server
      await fetch(`${API_URL}/api/push-notifications/unsubscribe?user_id=${userId}&endpoint=${encodeURIComponent(subscription.endpoint)}`, {
        method: 'POST'
      });

      setSubscription(null);
      setIsSubscribed(false);
      setLoading(false);
      return true;

    } catch (err) {
      console.error('Error unsubscribing:', err);
      setError(err.message);
      setLoading(false);
      return false;
    }
  }, [subscription, userId]);

  // Send test notification
  const sendTest = useCallback(async () => {
    if (!userId) return false;

    try {
      const response = await fetch(`${API_URL}/api/push-notifications/test?user_id=${userId}`, {
        method: 'POST'
      });
      
      const data = await response.json();
      return data.status === 'success';
    } catch (err) {
      console.error('Error sending test:', err);
      return false;
    }
  }, [userId]);

  return {
    isSupported,
    isSubscribed,
    permission,
    loading,
    error,
    subscribe,
    unsubscribe,
    sendTest
  };
}

export default usePushNotifications;
